import { createFileRoute, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, type FormEvent } from "react";
import { Check, FileText, Printer, PenLine, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Container } from "@/components/Container";
import { DISCLAIMER } from "@/lib/nav";
import { seo } from "@/lib/seo";
import { reviewScope, signScope } from "@/lib/client-scope.functions";

export const Route = createFileRoute("/scope/$token")({
  loader: async ({ params }) => {
    if (!/^[A-Za-z0-9_-]{43}$/.test(params.token)) throw notFound();
    const result = await reviewScope({ data: { token: params.token } });
    if (!result) throw notFound();
    return result;
  },
  head: ({ params }) => ({
    ...seo({
      title: "Pelly Health & Wellness: Scope of Work",
      description:
        "Private scope of work for Pelly Health & Wellness: website, patient booking, Stripe payments, email notifications, and project acceptance.",
      path: `/scope/${params.token}`,
      noindex: true,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "DigitalDocument",
        name: "Pelly Health & Wellness — Scope of Work",
      },
    }),
    meta: [
      ...seo({
        title: "Pelly Health & Wellness: Scope of Work",
        description:
          "Private scope of work for Pelly Health & Wellness: website, patient booking, Stripe payments, email notifications, and project acceptance.",
        path: `/scope/${params.token}`,
        noindex: true,
      }).meta,
      { name: "referrer", content: "no-referrer" },
      { name: "robots", content: "noindex, nofollow, noarchive" },
    ],
  }),
  component: ScopePage,
});

function ScopePage() {
  const initial = Route.useLoaderData();
  const { token } = Route.useParams();
  const [review, setReview] = useState(initial);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const sign = useServerFn(signScope);
  const refresh = useServerFn(reviewScope);
  const doc = review.document;
  const ownSignature = review.signatures.find((signature) => signature.role === review.role);
  const complete =
    review.signatures.length === 2 && review.signatures.every((signature) => signature.signedAt);
  const money = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  const displayTotal = couponApplied ? 0 : doc.total;

  function applyCoupon(event: FormEvent) {
    event.preventDefault();
    if (coupon.trim().toUpperCase() === "ASANGA") {
      setCouponApplied(true);
      setCouponError("");
    } else {
      setCouponError("That code is not valid for this document.");
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await sign({ data: { token, documentHash: review.documentHash, name, email, consent } });
      const updated = await refresh({ data: { token } });
      if (!updated) throw new Error("Please refresh to view your signed document.");
      setReview(updated);
    } catch (issue) {
      setError(
        issue instanceof Error ? issue.message : "Unable to save your signature. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-paper-50 text-ink-900">
      <header className="border-b border-ink-200 bg-paper-0">
        <Container className="flex flex-wrap items-center justify-between gap-4 py-5">
          <a
            href="https://laaconsulting.com"
            className="flex min-h-11 items-center gap-3"
            aria-label="LAA Consulting home"
          >
            <img src="/laa-mark.svg" width="36" height="36" alt="" />
            <span className="font-display text-xl font-semibold">LAA Consulting</span>
          </a>
          <span className="flex items-center gap-2 text-body-sm text-ink-600">
            <LockKeyhole size={16} strokeWidth={1.5} />
            Private client document
          </span>
        </Container>
      </header>
      <main id="main">
        <section className="bg-ink-900 py-12 text-paper-0 md:py-16 print:bg-paper-0 print:text-ink-900">
          <Container>
            <p className="flex items-center gap-2 font-mono text-xs uppercase text-brand-400">
              <FileText size={18} strokeWidth={1.5} />
              Scope of work · {doc.date}
            </p>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-tight tracking-normal md:text-5xl">
              {doc.client}
            </h1>
            <p className="mt-4 max-w-2xl text-body-lg text-ondark-body print:text-ink-600">
              {doc.title}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 border-t border-ondark-muted/30 pt-6">
              <div>
                <p className="text-body-sm text-ondark-muted print:text-ink-600">
                  Total project investment
                </p>
                <p className="mt-1 font-display text-3xl font-semibold tabular-nums">
                  {couponApplied && (
                    <span className="mr-3 text-xl font-normal text-ondark-muted line-through print:text-ink-600">
                      {money(doc.total)}
                    </span>
                  )}
                  {money(displayTotal)} <span className="text-sm font-normal">USD</span>
                </p>
                {couponApplied && (
                  <p className="mt-1 text-body-sm text-brand-400">Code ASANGA applied</p>
                )}
              </div>
              {!couponApplied && (
                <form onSubmit={applyCoupon} className="print:hidden">
                  <Label htmlFor="coupon" className="text-body-sm text-ondark-muted">
                    Coupon code
                  </Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      id="coupon"
                      value={coupon}
                      onChange={(event) => setCoupon(event.target.value)}
                      placeholder="Enter code"
                      autoComplete="off"
                      className="h-11 w-36 border-ondark-muted/40 bg-transparent text-paper-0 placeholder:text-ondark-muted"
                    />
                    <Button type="submit" variant="outline" size="sm" className="h-11 text-ink-900">
                      Apply
                    </Button>
                  </div>
                  {couponError && (
                    <p role="alert" className="mt-1 text-body-sm text-brand-400">
                      {couponError}
                    </p>
                  )}
                </form>
              )}
              <div>
                <p className="text-body-sm text-ondark-muted print:text-ink-600">Document status</p>
                <p className="mt-2 text-body">
                  {complete
                    ? "Accepted by both parties"
                    : ownSignature?.signedAt
                      ? "Your signature recorded"
                      : "Ready for review"}
                </p>
              </div>
              <div className="ml-auto flex flex-wrap gap-3 print:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-ink-900"
                  onClick={() => window.print()}
                >
                  <Printer size={18} />
                  Print / save PDF
                </Button>
                {!ownSignature?.signedAt && (
                  <Button asChild size="sm">
                    <a href="#acceptance">
                      <PenLine size={18} />
                      Review & sign
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </Container>
        </section>
        <Container className="py-12 md:py-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,760px)_220px] lg:gap-14">
            <article className="min-w-0">
              <dl className="mb-12 grid grid-cols-2 gap-6 border-b border-ink-200 pb-8 text-body-sm">
                <div>
                  <dt className="text-ink-600">Prepared for</dt>
                  <dd className="mt-1 font-medium">{doc.client}</dd>
                </div>
                <div>
                  <dt className="text-ink-600">Prepared by</dt>
                  <dd className="mt-1 font-medium">LAA Consulting</dd>
                </div>
                <div>
                  <dt className="text-ink-600">Date</dt>
                  <dd className="mt-1">{doc.date}</dd>
                </div>
                <div>
                  <dt className="text-ink-600">Version</dt>
                  <dd className="mt-1">{doc.version}</dd>
                </div>
              </dl>
              {doc.sections.map((section, index) => (
                <section
                  id={`section-${index}`}
                  key={section.heading}
                  className="mb-12 scroll-mt-8 break-inside-avoid-page"
                >
                  <h2 className="mb-5 font-display text-2xl font-semibold tracking-normal">
                    {section.heading}
                  </h2>
                  {section.items.map((item, itemIndex) =>
                    item.heading ? (
                      <h3
                        key={itemIndex}
                        className="mb-3 mt-7 font-display text-xl font-medium text-brand-700"
                      >
                        {item.text}
                      </h3>
                    ) : (
                      <p key={itemIndex} className="mb-3 text-body text-ink-600">
                        {item.text}
                      </p>
                    ),
                  )}
                </section>
              ))}
              <section id="investment" className="mb-12 scroll-mt-8">
                <h2 className="font-display text-2xl font-semibold tracking-normal">
                  6. Deliverables, timeline & investment
                </h2>
                <p className="mt-4 text-body text-ink-600">
                  Fixed project fee, allocated by the relative effort of each deliverable. Phase 1
                  is complete and in review. Remaining target dates will be agreed with Pelly.
                </p>
                <div className="mt-6 divide-y divide-ink-200 border-y border-ink-200">
                  {doc.deliverables.map((item) => (
                    <div key={item.name} className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 py-5">
                      <div>
                        <h3 className="font-medium text-ink-900">{item.name}</h3>
                        <p className="mt-1 text-body-sm text-ink-600">{item.includes}</p>
                        <p className="mt-2 text-body-sm text-brand-700">{item.status}</p>
                      </div>
                      <p className="font-mono text-body font-semibold tabular-nums">
                        {money(item.fee)}
                      </p>
                    </div>
                  ))}
                  <div className="flex justify-between gap-4 bg-brand-50 px-4 py-5 font-semibold">
                    <span>Total project fee · USD</span>
                    <span className="tabular-nums">{money(doc.total)}</span>
                  </div>
                </div>
                <p className="mt-5 text-body-sm text-ink-600">
                  Stripe charges its own processing fees, billed directly to Pelly. These are
                  separate from LAA Consulting’s fees.
                </p>
              </section>
              <section id="acceptance" className="scroll-mt-8 border-t border-ink-200 pt-10">
                <h2 className="font-display text-2xl font-semibold tracking-normal">
                  7. Acceptance
                </h2>
                <p className="mt-4 text-body text-ink-600">
                  By signing below, both parties agree to the scope described in this document. Any
                  change to the scope will be handled through a written change order.
                </p>
                <div className="mt-7 grid gap-6 sm:grid-cols-2">
                  {(["client", "consultant"] as const).map((role) => {
                    const signature = review.signatures.find((item) => item.role === role);
                    return (
                      <div key={role} className="border-b border-ink-200 pb-6">
                        <h3 className="font-medium">
                          {role === "client" ? doc.client : "LAA Consulting"}
                        </h3>
                        {signature?.signedAt ? (
                          <>
                            <p className="mt-4 flex items-center gap-2 text-brand-700">
                              <Check size={18} strokeWidth={1.5} />
                              Signed electronically
                            </p>
                            <p className="mt-2 text-xl font-medium">{signature.name}</p>
                            <p className="mt-1 text-body-sm text-ink-600">
                              {new Date(signature.signedAt).toLocaleString("en-US", {
                                timeZone: "UTC",
                              })}{" "}
                              UTC
                            </p>
                          </>
                        ) : (
                          <p className="mt-4 text-body-sm text-ink-600">Awaiting signature</p>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div aria-live="polite" className="mt-6">
                  {ownSignature?.signedAt && (
                    <p className="rounded-lg border border-brand-200 bg-brand-50 p-5 text-brand-800">
                      Your signature has been saved.{" "}
                      {complete
                        ? "Both parties have accepted this scope."
                        : "The other party’s signature is still pending."}
                    </p>
                  )}
                </div>
                {!ownSignature?.signedAt && (
                  <form onSubmit={onSubmit} className="mt-8 space-y-5 print:hidden">
                    <p className="font-medium">
                      Signing for {review.role === "client" ? doc.client : "LAA Consulting"}
                    </p>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="signature-name">Full name / electronic signature</Label>
                        <Input
                          id="signature-name"
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                          required
                          minLength={2}
                          maxLength={120}
                          autoComplete="name"
                          className="mt-2 min-h-12 text-base"
                        />
                      </div>
                      <div>
                        <Label htmlFor="signature-email">Email address</Label>
                        <Input
                          id="signature-email"
                          type="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          required
                          maxLength={254}
                          autoComplete="email"
                          className="mt-2 min-h-12 text-base"
                        />
                      </div>
                    </div>
                    <label
                      htmlFor="signature-consent"
                      className="flex min-h-11 cursor-pointer items-start gap-3 text-body-sm text-ink-600"
                    >
                      <Checkbox
                        id="signature-consent"
                        checked={consent}
                        onCheckedChange={(value) => setConsent(value === true)}
                        className="mt-1 shrink-0"
                      />
                      <span>
                        I am authorized to sign for{" "}
                        {review.role === "client" ? doc.client : "LAA Consulting"}. I agree to this
                        scope and the {money(displayTotal)} USD project fee, and consent to use my
                        typed name as my electronic signature.
                      </span>
                    </label>
                    {error && (
                      <p role="alert" className="text-body-sm text-destructive">
                        {error}
                      </p>
                    )}
                    <Button type="submit" disabled={busy || !consent}>
                      <PenLine size={18} />
                      {busy ? "Saving signature…" : "Accept & sign scope"}
                    </Button>
                  </form>
                )}
                <p className="mt-8 break-all font-mono text-xs text-ink-500">
                  Document fingerprint: {review.documentHash}
                </p>
              </section>
            </article>
            <aside className="order-first lg:order-last print:hidden">
              <nav aria-label="Document sections" className="lg:sticky lg:top-8">
                <p className="mb-4 text-body-sm font-medium text-ink-900">In this document</p>
                <div className="flex flex-wrap gap-x-5 lg:flex-col lg:gap-0">
                  {doc.sections.map((section, index) => (
                    <a
                      key={section.heading}
                      href={`#section-${index}`}
                      className="flex min-h-11 items-center text-body-sm text-ink-600 hover:text-brand-700 focus-visible:outline-brand-700"
                    >
                      {section.heading.replace(/^\d\. /, "")}
                    </a>
                  ))}
                  <a
                    href="#investment"
                    className="flex min-h-11 items-center text-body-sm text-ink-600 hover:text-brand-700"
                  >
                    Investment
                  </a>
                  <a
                    href="#acceptance"
                    className="flex min-h-11 items-center text-body-sm font-medium text-brand-700"
                  >
                    Acceptance
                  </a>
                </div>
              </nav>
            </aside>
          </div>
        </Container>
      </main>
      <footer className="border-t border-ink-200 bg-paper-0 py-8">
        <Container>
          <div className="flex flex-wrap justify-between gap-4 text-body-sm">
            <p className="font-medium">LAA Consulting</p>
            <a
              href="mailto:info@laaconsulting.com"
              className="text-brand-700 underline underline-offset-4"
            >
              info@laaconsulting.com
            </a>
          </div>
          <p className="mt-5 max-w-4xl text-xs leading-relaxed text-ink-600">{DISCLAIMER}</p>
        </Container>
      </footer>
    </div>
  );
}
